import { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import { Trans, useTranslation } from 'react-i18next';
// components
import Iconify from 'src/components/Iconify';
import CanaisConfig from './CanaisConfig';
import { CANAL_DETALHE_DESIGN as D } from './CanaisDetails.design';
// hooks
import useAuth from 'src/hooks/useAuth';
// actions
import v2_actionListUsersByClient from 'src/actions/v2/usuarios/v2_actionListUsersByClient';
import v2_actionListSectorsByClient from 'src/actions/v2/cliente/v2_actionListSectorsByClient';
import v2_actionDetalhesCanal from 'src/actions/v2/cliente/canais/v2_actionDetalhesCanal';
import v2_actionEditarCanal from 'src/actions/v2/cliente/canais/v2_actionEditarCanal';
import v1_actionTelegramConectar from 'src/actions/v1/telegram/v1_actionTelegramConectar';
import v1_actionTelegramDesconectar from 'src/actions/v1/telegram/v1_actionTelegramDesconectar';
import v2_actionMetaVincularContaSocial from 'src/actions/v2/meta/v2_actionMetaVincularContaSocial';
import v2_actionMetaDesvincularContaSocial from 'src/actions/v2/meta/v2_actionMetaDesvincularContaSocial';
import mktAccountsService from 'src/services/mktAccountsService';

// ----------------------------------------------------------------------

// Plataformas que aceitam vinculo com uma conta social, e o provider
// correspondente em lais_mkt_accounts. Espelha PROVIDER_POR_PLATFORM do
// backend (v2_meta.controller.js) — canal fora deste mapa nao mostra o bloco.
const PROVIDER_POR_PLATFORM = {
    messenger: 'facebook',
    instagram: 'instagram',
    whatsapp_business: 'whatsapp_business',
};

const VINCULO_UI = {
    messenger: {
        icone: 'ic:round-facebook',
        iconeConta: 'mdi:facebook',
    },
    instagram: {
        icone: 'mdi:instagram',
        iconeConta: 'mdi:instagram',
    },
    whatsapp_business: {
        icone: 'ic:round-whatsapp',
        iconeConta: 'mdi:whatsapp',
    },
};

// Ícone e fundo do ladrilho da plataforma, no topo e no cartão de conexão.
// O fundo é uma lavagem clara da cor da marca — vai por style inline porque
// cada canal traz a sua e não daria para gerar as classes estaticamente.
const PLATAFORMA_UI = {
    whatsapp: { icone: 'logos:whatsapp-icon', fundo: '#e7f8ef' },
    whatsapp_business: { icone: 'logos:whatsapp-icon', fundo: '#e7f8ef' },
    instagram: { icone: 'skill-icons:instagram', fundo: '#fdeef4' },
    messenger: { icone: 'logos:messenger', fundo: '#e8f0fe' },
    telegram: { icone: 'logos:telegram', fundo: '#e8f4fd' },
    webchat: { icone: 'streamline-plump:web', fundo: '#f0ebfd', cor: '#7c3aed' },
};

const PLATAFORMA_PADRAO = { icone: 'eva:message-circle-outline', fundo: '#f4f4f7', cor: '#71717a' };

// Rótulo + caixa de um campo do formulário, na altura real do input (44px).
function SkeletonCampo() {
    return (
        <div className={D.field}>
            <div className={`${D.skeleton.line} h-[13px] w-[104px] mb-[7px]`} />
            <div className={D.skeleton.input} />
        </div>
    );
}

// Esqueleto da carga inicial. Nada do canal é conhecido antes da resposta —
// nem a plataforma, que decide ícone, abas e blocos de conexão — então a tela
// inteira espera junto: sem isso o topo pisca "Novo Canal" com o ladrilho
// neutro e as abas trocam de conjunto no meio da leitura.
// Repete a malha real (barra fixa + abas + cartões) para o conteúdo entrar no
// lugar, sem salto.
function CanalDetalheSkeleton() {
    const S = D.skeleton;
    return (
        <div className={S.root} style={{ fontFamily: D.fontStack }} aria-busy="true">
            <div className={D.stickyHeader}>
                <div className={`${D.container} ${S.pulse}`}>
                    <div className={D.topbar}>
                        <div className={D.topbarIdentity}>
                            <div className={S.back} />
                            <div className={S.tile} />
                            <div className="min-w-0 flex-1 pt-px">
                                <div className={`${S.line} h-[18px] w-[190px]`} />
                                <div className={`${S.line} h-[13px] w-[132px] mt-[9px]`} />
                            </div>
                        </div>
                        <div className={D.topbarActions}>
                            <div className={`${S.line} h-[13px] w-[72px]`} />
                            <div className={`${S.button} w-[164px]`} />
                        </div>
                    </div>
                    {/* Quatro abas: o mínimo que toda plataforma tem
                        (Configuração, Mensagens, Atendimentos, IA). */}
                    <div className={D.tabsList}>
                        {[92, 84, 108, 24].map((largura) => (
                            <div
                                key={largura}
                                className={`${S.line} h-[15px] my-[11px] flex-shrink-0`}
                                style={{ width: largura }}
                            />
                        ))}
                    </div>
                </div>
            </div>

            <div className={`${D.container} ${D.tabsBody} ${D.pageBody}`}>
                {/* Mesma sequência da aba Configuração: rótulo de seção fora do
                    cartão, cartão com a grade de dois campos. */}
                <div className={`${D.stack} ${S.pulse}`}>
                    <div className={D.sectionHeader}>
                        <div className={`${S.line} h-[13px] w-[112px]`} />
                    </div>
                    <div className={D.card}>
                        <div className={D.grid2}>
                            <SkeletonCampo />
                            <SkeletonCampo />
                        </div>
                    </div>

                    <div className={D.sectionHeader}>
                        <div className={`${S.line} h-[13px] w-[148px]`} />
                    </div>
                    <div className={D.card}>
                        <div className={D.grid2}>
                            <SkeletonCampo />
                            <SkeletonCampo />
                            <SkeletonCampo />
                            <SkeletonCampo />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default function CanaisDetails() {
    const navigate = useNavigate();
    const { enqueueSnackbar } = useSnackbar();
    const { user, cliente } = useAuth();
    const location = useLocation();
    const { t } = useTranslation('settings');

    const [isEdit, setIsEdit] = useState(true);
    const [listUsers, setListUsers] = useState([]);
    const [listSectors, setListSectors] = useState([]);
    const [loading, setLoading] = useState(false);
    // Carga inicial da tela (detalhe do canal, usuários, setores e — quando a
    // plataforma tem provider — contas sociais). Enquanto for true a tela é só
    // o esqueleto.
    const [carregandoInicial, setCarregandoInicial] = useState(true);
    // Telegram: o token nunca volta do servidor, então o input vive só no cliente
    // e é limpo assim que a conexão é feita.
    const [telegramToken, setTelegramToken] = useState('');
    const [telegramLoading, setTelegramLoading] = useState(false);

    // Conta Social vinculada (Messenger/Instagram) — substitui a digitacao
    // manual de token/pageId. O bloco manual so fica acessivel em modo avancado.
    const [contasSociais, setContasSociais] = useState([]);
    const [loadingContasSociais, setLoadingContasSociais] = useState(false);
    const [contaSelecionada, setContaSelecionada] = useState('');
    const [vinculando, setVinculando] = useState(false);
    const [modoAvancado, setModoAvancado] = useState(false);
    // Telegram Business é opcional e quase sempre irrelevante — nasce fechado
    // para não empurrar o resto do formulário para baixo.
    const [businessAberto, setBusinessAberto] = useState(false);

    // Último estado conhecido como salvo. Serve para dois usos na barra de topo:
    // marcar "alterações não salvas" e restaurar o formulário no "Descartar".
    const baselineRef = useRef(null);
    const [savedAt, setSavedAt] = useState(null);
    // Só existe para re-renderizar o rótulo "salvo há N min" enquanto o tempo passa.
    const [, setRelogio] = useState(0);

    // Extrai o parâmetro de consulta "channel" da string de busca (search) da URL
    const params = new URLSearchParams(location.search);
    const channel = params.get('channelId');
    const isNew = params.get('new');

    const [canal, setCanal] = useState({
        nome: '',
        status: true,
        // Vazio até getDetailsChannel — senão abas só de WhatsApp (Saúde, etc.)
        // aparecem por engano em telegram/messenger/instagram no primeiro render.
        platform: '',
        date: new Date(),
        principal: true,
        sessao: '',
        pod: '',
        descontinuado: false,
        webhook_type: '',
        mkt_account_id: null,
    });

    const [configAi, setConfigAi] = useState({
        prompt: '',
        provider: '',
        openai: { apiKey: '', organization: '', model: '' },
        gemini: { apiKey: '', model: '' },
        modules: {
            aiAnalysisChat: {
                customPrompts: false,
                prompt_alerts: '',
                prompt_predictive: '',
                prompt_resume: '',
                prompt_sentiment: '',
                prompt_subject: '',
                prompt_suggestion: '',
            },
        },
        allowsInChat: [],
        messages: {
            msgAfterTargeting: '',
            msgBreakFiles: t('channels.details.aiDefaults.msgBreakFiles'),
            msgBreakAudios: t('channels.details.aiDefaults.msgBreakAudios'),
            msgImages: t('channels.details.aiDefaults.msgImages'),
            messageCallback: t('channels.details.aiDefaults.messageCallback'),
        },
    });

    const [config, setConfig] = useState({
        token: '',
        usuario: '',
        sessao: '',
        dialogflow: { privateKey: '', status: true, projectId: '', clientEmail: '' },
        direcionamento_direto: {
            direcionado_para: '',
            direciona_setor: [],
            direciona_usuario: [],
            statusRoom: '',
            mensagem_apos_cadastro: '',
        },
        aplicativo: { status: true },
        meta: { whatsappBusinness: { fromNumberId: '', accessToken: '', wabaId: '' } },
        // Canal telegram — botToken nunca é devolvido pelo servidor.
        telegram: { botId: '', botUsername: '', botName: '', businessEnabled: false },
        incomingcall: { status: true, message: '' },
        flowbuilder: { flowId: '' },
        chatflow: { chatflow_id: '' },
        ai: configAi,
        laisGo: { status: false },
        departamentConfig: { allocate: { status: false, type: 'round_robin' }, wallet: { status: false } },
        // Identificação do visitante (pré-chat) — usado apenas pelo canal webchat.
        preChat: { enabled: false, title: '', subtitle: '', fields: [] },
        // Horário de atendimento — usado apenas pelo canal webchat.
        businessHours: { enabled: false, timezone: 'America/Sao_Paulo', outsideMessage: '', schedules: [] },
    });

    let makeChannel = { ...canal, config: config };

    async function getDetailsChannel() {
        try {
            const response = await v2_actionDetalhesCanal(channel);
            const responseChannel = response.data.data;
            if (responseChannel) {
                const nextCanal = {
                    ...canal,
                    _id: responseChannel._id,
                    nome: responseChannel.nome,
                    platform: responseChannel.platform,
                    webhook_type: responseChannel.webhook_type,
                    cliente_id: responseChannel.cliente_id,
                    mkt_account_id: responseChannel.mkt_account_id || null,
                };
                setCanal(nextCanal);

                // Mescla a configuração do servidor com a estrutura inicial
                const serverConfig = responseChannel.config || {};

                // Converter feedbackTemplateVariaveis de objeto para array se necessário
                let feedbackTemplateVariaveis = serverConfig.generalConfig?.feedbackTemplateVariaveis;
                if (feedbackTemplateVariaveis && !Array.isArray(feedbackTemplateVariaveis)) {
                    const numerosOrdenados = Object.keys(feedbackTemplateVariaveis).sort((a, b) => parseInt(a) - parseInt(b));
                    const maxNumero =
                        numerosOrdenados.length > 0 ? parseInt(numerosOrdenados[numerosOrdenados.length - 1]) : 0;
                    const arrayVariaveis = [];
                    for (let i = 1; i <= maxNumero; i++) {
                        arrayVariaveis.push(feedbackTemplateVariaveis[String(i)] || '');
                    }
                    feedbackTemplateVariaveis = arrayVariaveis;
                } else if (Array.isArray(feedbackTemplateVariaveis)) {
                    if (
                        feedbackTemplateVariaveis.length > 0 &&
                        typeof feedbackTemplateVariaveis[0] === 'object' &&
                        feedbackTemplateVariaveis[0].numero
                    ) {
                        const maxNumero = Math.max(...feedbackTemplateVariaveis.map((v) => parseInt(v.numero)));
                        const arrayVariaveis = [];
                        for (let i = 1; i <= maxNumero; i++) {
                            const variavel = feedbackTemplateVariaveis.find((v) => v.numero === String(i));
                            arrayVariaveis.push(variavel?.valor || '');
                        }
                        feedbackTemplateVariaveis = arrayVariaveis;
                    }
                } else if (!feedbackTemplateVariaveis) {
                    feedbackTemplateVariaveis = [];
                }

                let transferTemplateVariaveis = serverConfig.generalConfig?.transferTemplateVariaveis;
                if (transferTemplateVariaveis && !Array.isArray(transferTemplateVariaveis)) {
                    // Mantém como objeto no editor de canais (mesmo formato do feedback no front)
                } else if (!transferTemplateVariaveis) {
                    transferTemplateVariaveis = {};
                }

                const nextConfig = {
                    ...config,
                    ...serverConfig,
                    generalConfig: {
                        ...(serverConfig.generalConfig || {}),
                        feedbackTemplateVariaveis: feedbackTemplateVariaveis,
                        transferTemplateVariaveis,
                    },
                    ai: {
                        ...config.ai,
                        ...(serverConfig.ai || {}),
                        modules: {
                            ...(config.ai?.modules || {}),
                            ...(serverConfig.ai?.modules || {}),
                            aiAnalysisChat: {
                                customPrompts: false,
                                prompt_alerts: '',
                                prompt_predictive: '',
                                prompt_resume: '',
                                prompt_sentiment: '',
                                prompt_subject: '',
                                prompt_suggestion: '',
                                ...(serverConfig.ai?.modules?.aiAnalysisChat || {}),
                            },
                        },
                    },
                };
                setConfig(nextConfig);
                marcarComoSalvo(nextCanal, nextConfig);
                // A plataforma volta daqui porque a carga inicial precisa dela
                // antes do setCanal chegar ao estado, para saber se ainda falta
                // buscar contas sociais.
                return nextCanal.platform;
            }
        } catch (error) {
            console.log(error);
        }
        return null;
    }

    // Referência do que está no servidor. Recalculada a cada carga e a cada save
    // — é contra ela que a barra de topo decide se há alterações pendentes.
    function marcarComoSalvo(canalSalvo, configSalvo) {
        baselineRef.current = {
            canal: canalSalvo,
            config: configSalvo,
            json: JSON.stringify({ canal: canalSalvo, config: configSalvo }),
        };
    }

    function descartarAlteracoes() {
        if (!baselineRef.current) return;
        setCanal(baselineRef.current.canal);
        setConfig(baselineRef.current.config);
    }

    async function createChannel() {
        try {
            let data = { action: 'editar_canal_front', scope: makeChannel };
            setLoading(true);
            await v2_actionEditarCanal(channel, data);
            marcarComoSalvo(canal, config);
            setSavedAt(Date.now());
            enqueueSnackbar(t('channels.details.toastSaveSuccess', 'Canal salvo com sucesso'), { variant: 'success' });
            setLoading(false);
        } catch (error) {
            setLoading(false);
            enqueueSnackbar(t('channels.details.toastSaveError', 'Erro ao salvar canal'), { variant: 'error' });
            console.log(error);
        }
    }

    // O canal é considerado conectado quando o servidor já resolveu o bot
    // (botUsername só é gravado depois de um getMe bem-sucedido).
    const telegramConectado = !!config.telegram?.botUsername;
    // Estado da conta Telegram Business — preenchido pelo update `business_connection`
    // quando o dono vincula o bot no app; nada aqui é editável pelo painel.
    const telegramBusiness = config.telegram;

    async function conectarTelegram() {
        if (!telegramToken.trim()) {
            enqueueSnackbar(t('channels.details.telegram.tokenRequired', 'Informe o token do bot'), { variant: 'warning' });
            return;
        }
        try {
            setTelegramLoading(true);
            const response = await v1_actionTelegramConectar({ channelId: channel, token: telegramToken.trim() });
            const bot = response.data.data;
            setTelegramToken('');
            // Recarrega do servidor em vez de remendar o estado local: "Salvar
            // Configurações" reescreve o config inteiro, e um estado defasado
            // apagaria o botToken/webhookSecret recém-gravados.
            await getDetailsChannel();
            enqueueSnackbar(
                t('channels.details.telegram.connectedSuccess', 'Bot @{{username}} conectado com sucesso', { username: bot.username }),
                { variant: 'success' }
            );
        } catch (error) {
            enqueueSnackbar(
                error?.response?.data?.message || t('channels.details.telegram.connectError', 'Erro ao conectar o bot do Telegram'),
                { variant: 'error' }
            );
        } finally {
            setTelegramLoading(false);
        }
    }

    async function desconectarTelegram() {
        try {
            setTelegramLoading(true);
            await v1_actionTelegramDesconectar({ channelId: channel });
            await getDetailsChannel();
            enqueueSnackbar(t('channels.details.telegram.disconnectedSuccess', 'Bot desconectado'), { variant: 'success' });
        } catch (error) {
            enqueueSnackbar(
                error?.response?.data?.message || t('channels.details.telegram.disconnectError', 'Erro ao desconectar o bot'),
                { variant: 'error' }
            );
        } finally {
            setTelegramLoading(false);
        }
    }

    async function getListUsers() {
        try {
            const response = await v2_actionListUsersByClient(user.clienteId);
            setListUsers(response.data.data);
        } catch (error) {
            console.log(error);
        }
    }

    async function getListSectors() {
        try {
            const response = await v2_actionListSectorsByClient(user.clienteId);
            setListSectors(response.data.data);
        } catch (error) {
            console.log(error);
        }
    }

    async function handleOnChangeChannel(e, newValue) {
        const { name, value } = e.target;
        switch (name) {
            case 'channelName':
                setCanal({ ...canal, nome: value });
                break;
            case 'statusRoomDirectTarget':
                setConfig({ ...config, direcionamento_direto: { ...config.direcionamento_direto, statusRoom: value } });
                break;
            case 'channelType':
                setCanal({ ...canal, platform: value });
                break;
            case 'dialogflowPrivateKey':
                setConfig({ ...config, dialogflow: { ...config.dialogflow, privateKey: value } });
                break;
            case 'dialogflowProjectId':
                setConfig({ ...config, dialogflow: { ...config.dialogflow, projectId: value } });
                break;
            case 'dialogflowClientEmail':
                setConfig({ ...config, dialogflow: { ...config.dialogflow, clientEmail: value } });
                break;
            case 'providerAi':
                setConfig({ ...config, ai: { ...(config.ai || {}), provider: value } });
                break;
            case 'openaiApiKey':
                setConfig({ ...config, ai: { ...(config.ai || {}), openai: { ...(config.ai?.openai || {}), apiKey: value } } });
                break;
            case 'openaiOrganization':
                setConfig({
                    ...config,
                    ai: { ...(config.ai || {}), openai: { ...(config.ai?.openai || {}), organization: value } },
                });
                break;
            case 'geminiApiKey':
                setConfig({ ...config, ai: { ...(config.ai || {}), gemini: { ...(config.ai?.gemini || {}), apiKey: value } } });
                break;
            case 'modelGemini':
                setConfig({ ...config, ai: { ...(config.ai || {}), gemini: { ...(config.ai?.gemini || {}), model: value } } });
                break;
            case 'messageCallback':
                setConfig({
                    ...config,
                    ai: { ...(config.ai || {}), messages: { ...(config.ai?.messages || {}), messageCallback: value } },
                });
                break;
            case 'modelOpenai':
                setConfig({ ...config, ai: { ...(config.ai || {}), openai: { ...(config.ai?.openai || {}), model: value } } });
                break;
            case 'apiOpenai':
                setConfig({ ...config, ai: { ...(config.ai || {}), openai: { ...(config.ai?.openai || {}), apiKey: value } } });
                break;
            case 'apiKeyGemini':
                setConfig({ ...config, ai: { ...(config.ai || {}), gemini: { ...(config.ai?.gemini || {}), apiKey: value } } });
                break;
            case 'apiKeyOpenai':
                setConfig({ ...config, ai: { ...(config.ai || {}), openai: { ...(config.ai?.openai || {}), apiKey: value } } });
                break;
            case 'organizationOpenai':
                setConfig({
                    ...config,
                    ai: { ...(config.ai || {}), openai: { ...(config.ai?.openai || {}), organization: value } },
                });
                break;
            case 'msgAfterTargeting':
                setConfig({
                    ...config,
                    ai: { ...(config.ai || {}), messages: { ...(config.ai?.messages || {}), msgAfterTargeting: value } },
                });
                break;
            case 'msgBreakFiles':
                setConfig({
                    ...config,
                    ai: { ...(config.ai || {}), messages: { ...(config.ai?.messages || {}), msgBreakFiles: value } },
                });
                break;
            case 'msgBreakAudios':
                setConfig({
                    ...config,
                    ai: { ...(config.ai || {}), messages: { ...(config.ai?.messages || {}), msgBreakAudios: value } },
                });
                break;
            case 'msgBreakImages':
                setConfig({
                    ...config,
                    ai: { ...(config.ai || {}), messages: { ...(config.ai?.messages || {}), msgImages: value } },
                });
                break;
            case 'prompt':
                setConfig({ ...config, ai: { ...(config.ai || {}), prompt: value } });
                break;
            case 'directTargetMessage':
                setConfig({
                    ...config,
                    direcionamento_direto: { ...config.direcionamento_direto, mensagem_apos_cadastro: value },
                });
                break;
            case 'laisai_v2.ai_agent_id':
                setConfig({ ...config, laisai_v2: { ...config.laisai_v2, ai_agent_id: value } });
                break;
            case 'generalConfig.msgs.finish':
                setConfig({
                    ...config,
                    generalConfig: { ...(config.generalConfig || {}), msgs: { ...(config.generalConfig?.msgs || {}), finish: value } },
                });
                break;
            case 'generalConfig.msgs.feedback':
                setConfig({
                    ...config,
                    generalConfig: {
                        ...(config.generalConfig || {}),
                        msgs: { ...(config.generalConfig?.msgs || {}), feedback: value },
                    },
                });
                break;
            case 'generalConfig.msgs.welcome':
                setConfig({
                    ...config,
                    generalConfig: {
                        ...(config.generalConfig || {}),
                        msgs: { ...(config.generalConfig?.msgs || {}), welcome: value },
                    },
                });
                break;
            case 'generalConfig.msgs.transfer':
                setConfig({
                    ...config,
                    generalConfig: {
                        ...(config.generalConfig || {}),
                        msgs: { ...(config.generalConfig?.msgs || {}), transfer: value },
                    },
                });
                break;
            case 'generalConfig.msgs.afterTarget':
                setConfig({
                    ...config,
                    generalConfig: {
                        ...(config.generalConfig || {}),
                        msgs: { ...(config.generalConfig?.msgs || {}), afterTarget: value },
                    },
                });
                break;
            case 'generalConfig.msgTransfer':
                setConfig({ ...config, generalConfig: { ...(config.generalConfig || {}), msgTransfer: value } });
                break;
            case 'generalConfig.msgFinish':
                setConfig({ ...config, generalConfig: { ...(config.generalConfig || {}), msgFinish: value } });
                break;
            case 'generalConfig.feedback':
                setConfig({ ...config, generalConfig: { ...(config.generalConfig || {}), feedback: value } });
                break;
            case 'generalConfig.feedbackTemplate':
                console.log('🟢 CanaisDetails: Atualizando feedbackTemplate', value);
                setConfig((prevConfig) => {
                    const newConfig = {
                        ...prevConfig,
                        generalConfig: { ...(prevConfig.generalConfig || {}), feedbackTemplate: value },
                    };
                    console.log('🟢 CanaisDetails: Novo config', newConfig.generalConfig?.feedbackTemplate);
                    return newConfig;
                });
                break;
            case 'generalConfig.feedbackTemplateVariaveis':
                console.log('🟢 CanaisDetails: Atualizando feedbackTemplateVariaveis', value);
                setConfig((prevConfig) => {
                    const newConfig = {
                        ...prevConfig,
                        generalConfig: { ...(prevConfig.generalConfig || {}), feedbackTemplateVariaveis: value },
                    };
                    console.log('🟢 CanaisDetails: Novo config variáveis', newConfig.generalConfig?.feedbackTemplateVariaveis);
                    return newConfig;
                });
                break;
            case 'generalConfig.transferTemplate':
                setConfig((prevConfig) => ({
                    ...prevConfig,
                    generalConfig: { ...(prevConfig.generalConfig || {}), transferTemplate: value },
                }));
                break;
            case 'generalConfig.transferTemplateVariaveis':
                setConfig((prevConfig) => ({
                    ...prevConfig,
                    generalConfig: { ...(prevConfig.generalConfig || {}), transferTemplateVariaveis: value },
                }));
                break;
            case 'laisGo.status':
                setConfig({ ...config, laisGo: { ...(config.laisGo || {}), status: value } });
                break;
            case 'departamentConfig.allocate.status':
                // Se allocate.status for true, desabilita wallet.status
                const newWalletStatus = value === true ? false : config.departamentConfig?.wallet?.status;
                setConfig({
                    ...config,
                    departamentConfig: {
                        ...(config.departamentConfig || {}),
                        allocate: { ...(config.departamentConfig?.allocate || {}), status: value },
                        wallet: { ...(config.departamentConfig?.wallet || {}), status: newWalletStatus },
                    },
                });
                break;
            case 'departamentConfig.allocate.type':
                setConfig({
                    ...config,
                    departamentConfig: {
                        ...(config.departamentConfig || {}),
                        allocate: { ...(config.departamentConfig?.allocate || {}), type: value },
                    },
                });
                break;
            case 'departamentConfig.allocate.period':
                setConfig({
                    ...config,
                    departamentConfig: {
                        ...(config.departamentConfig || {}),
                        allocate: { ...(config.departamentConfig?.allocate || {}), period: value },
                    },
                });
                break;
            case 'departamentConfig.defaultSector':
                setConfig({ ...config, departamentConfig: { ...(config.departamentConfig || {}), defaultSector: value } });
                break;
            case 'departamentConfig.allocate.ignoreUsersOff':
                setConfig({
                    ...config,
                    departamentConfig: {
                        ...(config.departamentConfig || {}),
                        allocate: { ...(config.departamentConfig?.allocate || {}), ignoreUsersOff: value },
                    },
                });
                break;
            case 'ai.modules.aiAnalysisChat.operation':
                setConfig({
                    ...config,
                    ai: {
                        ...config.ai,
                        modules: {
                            ...(config.ai.modules || {}),
                            aiAnalysisChat: { ...(config.ai.modules?.aiAnalysisChat || {}), operation: value },
                        },
                    },
                });
                break;
            case 'ai.modules.aiAnalysisChat.numberMsgs':
                setConfig({
                    ...config,
                    ai: {
                        ...config.ai,
                        modules: {
                            ...(config.ai.modules || {}),
                            aiAnalysisChat: { ...(config.ai.modules?.aiAnalysisChat || {}), numberMsgs: value },
                        },
                    },
                });
                break;
            case 'ai.modules.aiAnalysisChat.status':
                setConfig({
                    ...config,
                    ai: {
                        ...config.ai,
                        modules: {
                            ...(config.ai.modules || {}),
                            aiAnalysisChat: { ...(config.ai.modules?.aiAnalysisChat || {}), status: value },
                        },
                    },
                });
                break;
            case 'ai.autoTranscribeAudio':
                setConfig({ ...config, ai: { ...(config.ai || {}), autoTranscribeAudio: value } });
                break;
            case 'ai.autoTranscribeImage':
                setConfig({ ...config, ai: { ...(config.ai || {}), autoTranscribeImage: value } });
                break;
            case 'departamentConfig.wallet.status':
                // Se wallet.status for true, desabilita allocate.status
                const newAllocateStatus = value === true ? false : config.departamentConfig?.allocate?.status;
                setConfig({
                    ...config,
                    departamentConfig: {
                        ...(config.departamentConfig || {}),
                        wallet: { ...(config.departamentConfig?.wallet || {}), status: value },
                        allocate: { ...(config.departamentConfig?.allocate || {}), status: newAllocateStatus },
                    },
                });
                break;
            case 'ai.modules.aiAnalysisChat.customPrompts':
                setConfig({
                    ...config,
                    ai: {
                        ...config.ai,
                        modules: {
                            ...(config.ai.modules || {}),
                            aiAnalysisChat: { ...(config.ai.modules?.aiAnalysisChat || {}), customPrompts: value },
                        },
                    },
                });
                break;
            case 'ai.modules.aiAnalysisChat.prompt_alerts':
                setConfig({
                    ...config,
                    ai: {
                        ...config.ai,
                        modules: {
                            ...(config.ai.modules || {}),
                            aiAnalysisChat: { ...(config.ai.modules?.aiAnalysisChat || {}), prompt_alerts: value },
                        },
                    },
                });
                break;
            case 'ai.modules.aiAnalysisChat.prompt_predictive':
                setConfig({
                    ...config,
                    ai: {
                        ...config.ai,
                        modules: {
                            ...(config.ai.modules || {}),
                            aiAnalysisChat: { ...(config.ai.modules?.aiAnalysisChat || {}), prompt_predictive: value },
                        },
                    },
                });
                break;
            case 'ai.modules.aiAnalysisChat.prompt_resume':
                setConfig({
                    ...config,
                    ai: {
                        ...config.ai,
                        modules: {
                            ...(config.ai.modules || {}),
                            aiAnalysisChat: { ...(config.ai.modules?.aiAnalysisChat || {}), prompt_resume: value },
                        },
                    },
                });
                break;
            case 'ai.modules.aiAnalysisChat.prompt_sentiment':
                setConfig({
                    ...config,
                    ai: {
                        ...config.ai,
                        modules: {
                            ...(config.ai.modules || {}),
                            aiAnalysisChat: { ...(config.ai.modules?.aiAnalysisChat || {}), prompt_sentiment: value },
                        },
                    },
                });
                break;
            case 'ai.modules.aiAnalysisChat.prompt_subject':
                setConfig({
                    ...config,
                    ai: {
                        ...config.ai,
                        modules: {
                            ...(config.ai.modules || {}),
                            aiAnalysisChat: { ...(config.ai.modules?.aiAnalysisChat || {}), prompt_subject: value },
                        },
                    },
                });
                break;
            case 'ai.modules.aiAnalysisChat.prompt_suggestion':
                setConfig({
                    ...config,
                    ai: {
                        ...config.ai,
                        modules: {
                            ...(config.ai.modules || {}),
                            aiAnalysisChat: { ...(config.ai.modules?.aiAnalysisChat || {}), prompt_suggestion: value },
                        },
                    },
                });
                break;
            case 'chatflow.chatflow_id':
                setConfig({ ...config, chatflow: { ...(config.chatflow || {}), chatflow_id: value } });
                break;
            case 'whatsapp_business_id':
                setConfig({
                    ...config,
                    meta: {
                        ...config.meta,
                        whatsappBusinness: { ...(config.meta?.whatsappBusinness || {}), fromNumberId: value },
                    },
                });
                break;
            case 'whatsapp_business_token':
                setConfig({
                    ...config,
                    meta: {
                        ...config.meta,
                        whatsappBusinness: { ...(config.meta?.whatsappBusinness || {}), accessToken: value },
                    },
                });
                break;
            case 'wabaId':
                setConfig({
                    ...config,
                    meta: { ...config.meta, whatsappBusinness: { ...(config.meta?.whatsappBusinness || {}), wabaId: value } },
                });
                break;
            case 'sla_tempo_aceite':
                setConfig({ ...config, sla_tempo_aceite: value ? Number(value) * 60 : '' });
                break;
            case 'aiSchedulePause.status':
                setConfig({
                    ...config,
                    aiSchedulePause: { ...(config.aiSchedulePause || {}), status: value },
                });
                break;
            case 'aiSchedulePause.action':
                setConfig({
                    ...config,
                    aiSchedulePause: { ...(config.aiSchedulePause || {}), action: value },
                });
                break;
            case 'aiSchedulePause.direciona_usuarios':
                setConfig({
                    ...config,
                    aiSchedulePause: { ...(config.aiSchedulePause || {}), direciona_usuarios: value },
                });
                break;
            case 'aiSchedulePause.direciona_setores':
                setConfig({
                    ...config,
                    aiSchedulePause: { ...(config.aiSchedulePause || {}), direciona_setores: value },
                });
                break;
            case 'aiSchedulePause.schedules':
                setConfig({
                    ...config,
                    aiSchedulePause: { ...(config.aiSchedulePause || {}), schedules: value },
                });
                break;
            case 'preChat.enabled':
                setConfig({ ...config, preChat: { ...(config.preChat || {}), enabled: value } });
                break;
            case 'preChat.title':
                setConfig({ ...config, preChat: { ...(config.preChat || {}), title: value } });
                break;
            case 'preChat.subtitle':
                setConfig({ ...config, preChat: { ...(config.preChat || {}), subtitle: value } });
                break;
            case 'preChat.fields':
                // value é o array completo de campos (substitui o anterior).
                setConfig({ ...config, preChat: { ...(config.preChat || {}), fields: value } });
                break;
            case 'businessHours.enabled':
                setConfig({ ...config, businessHours: { ...(config.businessHours || {}), enabled: value } });
                break;
            case 'businessHours.timezone':
                setConfig({ ...config, businessHours: { ...(config.businessHours || {}), timezone: value } });
                break;
            case 'businessHours.outsideMessage':
                setConfig({ ...config, businessHours: { ...(config.businessHours || {}), outsideMessage: value } });
                break;
            case 'businessHours.schedules':
                // value é o array completo de faixas (substitui o anterior).
                setConfig({ ...config, businessHours: { ...(config.businessHours || {}), schedules: value } });
                break;
        }
    }

    async function handleOnChangeAutomCompleteChannel(e, newValue, name) {
        const mapping = newValue.map((item) => item._id);
        switch (name) {
            case 'directTargetUser':
                setConfig({
                    ...config,
                    direcionamento_direto: { ...config.direcionamento_direto, direciona_usuario: mapping },
                });
                break;
            case 'directTargetSector':
                setConfig({ ...config, direcionamento_direto: { ...config.direcionamento_direto, direciona_setor: mapping } });
                break;
            default:
                break;
        }
    }

    // Atualiza um campo de config.meta.<platform> (instagram | messenger) — conexao manual.
    function updateMetaField(platform, field, value) {
        setConfig((prev) => ({
            ...prev,
            meta: {
                ...(prev.meta || {}),
                [platform]: { ...(prev.meta?.[platform] || {}), [field]: value },
            },
        }));
    }

    // Contas sociais elegiveis para este canal — provider precisa bater com a
    // plataforma (messenger->facebook, instagram->instagram,
    // whatsapp_business->whatsapp_business). Refeita sempre que a plataforma
    // do canal muda (inclusive na primeira carga, quando o platform inicial
    // 'whatsapp' vira o real apos getDetailsChannel).
    //
    // Sem includePending de proposito: conexao de WhatsApp incompleta (sem
    // numero ou sem PIN) nao pode ser vinculada — o canal nasceria mudo.
    // platform vem por parâmetro na carga inicial: lá o valor acabou de chegar
    // do servidor e ainda não está em canal.platform.
    async function getContasSociais(platform = canal.platform) {
        const provider = PROVIDER_POR_PLATFORM[platform];
        if (!provider) return;
        setLoadingContasSociais(true);
        try {
            const res = await mktAccountsService.listar(user.clienteId, { provider });
            setContasSociais(res?.data || []);
        } catch (error) {
            console.log(error);
        } finally {
            setLoadingContasSociais(false);
        }
    }

    async function vincularContaSocial() {
        if (!contaSelecionada) return;
        setVinculando(true);
        try {
            const response = await v2_actionMetaVincularContaSocial(canal._id, { mkt_account_id: contaSelecionada, user_id: user?.id });
            const data = response.data?.data;

            if (data?.requires_instagram_login) {
                enqueueSnackbar(
                    t('channels.details.meta.linkedNeedsLogin', 'Conta vinculada — agora conecte as mensagens do Instagram para concluir.'),
                    { variant: 'info' }
                );
            } else {
                enqueueSnackbar(t('channels.details.meta.linkedSuccess', 'Conta social vinculada com sucesso'), { variant: 'success' });
            }
            (data?.warnings || []).forEach((w) => enqueueSnackbar(w, { variant: 'warning' }));

            setContaSelecionada('');
            await getDetailsChannel();
            await getContasSociais();
        } catch (error) {
            enqueueSnackbar(
                error?.response?.data?.mensagem || t('channels.details.meta.linkError', 'Erro ao vincular conta social'),
                { variant: 'error' }
            );
        } finally {
            setVinculando(false);
        }
    }

    async function desvincularContaSocial() {
        setVinculando(true);
        try {
            await v2_actionMetaDesvincularContaSocial(canal._id);
            enqueueSnackbar(t('channels.details.meta.unlinkedSuccess', 'Conta social desvinculada'), { variant: 'success' });
            await getDetailsChannel();
            await getContasSociais();
        } catch (error) {
            enqueueSnackbar(
                error?.response?.data?.mensagem || t('channels.details.meta.unlinkError', 'Erro ao desvincular conta social'),
                { variant: 'error' }
            );
        } finally {
            setVinculando(false);
        }
    }

    // Instagram Login for Business — segundo passo apos vincular. O vinculo ja
    // gravou a identidade (instagramUserId/username), mas o token de mensageria
    // e' de um app diferente (ver comentario em v2_meta.controller.vincularContaSocial)
    // e so vem por aqui. Mesma funcao de Configuracoes/index.js:abrirInstagramOAuth.
    function conectarInstagramMessaging() {
        const appId = import.meta.env.VITE_INSTAGRAM_APP_ID || '1634440460959057';
        const redirectUri = import.meta.env.VITE_INSTAGRAM_AUTH_REDIRECT_URI
            || (import.meta.env.VITE_META_AUTH_REDIRECT_URI || '').replace('/webhook/v1/auth', '/webhook/v1/auth/instagram')
            || 'https://webhook-beta.lais.app/webhook/v1/auth/instagram';
        const scope = 'instagram_business_basic,instagram_business_manage_messages,instagram_business_manage_comments,instagram_business_content_publish,instagram_business_manage_insights';
        const url = `https://www.instagram.com/oauth/authorize?force_reauth=true&client_id=${appId}&state=${canal._id}&redirect_uri=${encodeURIComponent(redirectUri)}&scope=${encodeURIComponent(scope)}&response_type=code`;
        window.location.assign(url);
    }

    // Carga inicial completa: o esqueleto só sai quando tudo o que o formulário
    // renderiza já chegou. As contas sociais ficam depois do detalhe porque
    // dependem da plataforma do canal.
    useEffect(() => {
        let ativo = true;
        (async () => {
            try {
                const [platform] = await Promise.all([getDetailsChannel(), getListUsers(), getListSectors()]);
                if (!ativo) return;
                await getContasSociais(platform);
            } finally {
                if (ativo) setCarregandoInicial(false);
            }
        })();
        return () => {
            ativo = false;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Recarrega as contas quando a plataforma muda depois da carga inicial —
    // durante ela a busca já acontece no efeito acima, com o valor recém-lido.
    useEffect(() => {
        if (carregandoInicial) return;
        getContasSociais();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [canal.platform]);

    // "salvo há N min" precisa envelhecer sozinho; sem save nesta sessão não há
    // o que contar e o intervalo nem chega a existir.
    useEffect(() => {
        if (!savedAt) return undefined;
        const id = setInterval(() => setRelogio((n) => n + 1), 30000);
        return () => clearInterval(id);
    }, [savedAt]);

    // Nome técnico do campo que o webhook usa para localizar o canal (Meta).
    const idFieldName =
        canal.platform === 'instagram'
            ? t('channels.details.meta.instagramUserIdLabel')
            : t('channels.details.meta.pageIdLabel');

    // A conta vinculada precisa estar na lista carregada (mesmo provider) para
    // exibir nome/avatar — se não estiver (foi desconectada em Contas Sociais
    // depois do vínculo), mostramos um aviso em vez de travar a tela.
    const contaVinculada = canal.mkt_account_id
        ? contasSociais.find((c) => String(c._id) === String(canal.mkt_account_id))
        : null;
    // Textos/icones do bloco de vinculo. Ausente = plataforma que nao aceita
    // conta social (webchat, telegram, whatsapp por QR...).
    const vinculoUi = VINCULO_UI[canal.platform];
    // Instagram: o vínculo grava só a identidade: o accessToken de mensageria
    // vem do Instagram Login (conectarInstagramMessaging), passo separado.
    const instagramMensageriaAtiva = Boolean(config.meta?.instagram?.accessToken);

    const plataformaNome = t(`channels.details.platforms.${canal.platform}`, { defaultValue: canal.platform });

    // Só há alterações pendentes depois que o canal carregou — antes disso a
    // referência não existe e a barra de topo mostra o estado neutro.
    const alteracoesPendentes = baselineRef.current
        ? JSON.stringify({ canal, config }) !== baselineRef.current.json
        : false;
    // Sem referência (canal ainda carregando, carga falhou ou canal novo) o
    // salvar continua liberado: travar o botão aí deixaria a tela sem saída.
    const podeSalvar = !baselineRef.current || alteracoesPendentes;

    function rotuloSalvo() {
        if (!savedAt) return t('channels.details.upToDate', 'Tudo salvo');
        const minutos = Math.floor((Date.now() - savedAt) / 60000);
        if (minutos < 1) return t('channels.details.savedJustNow', 'salvo agora');
        return t('channels.details.savedAgo', 'salvo há {{minutos}} min', { minutos });
    }

    // Estado de conexão exibido ao lado do nome. Cada plataforma tem sua própria
    // evidência de "conectado"; onde não existe evidência, não inventamos badge.
    function statusConexao() {
        if (canal.platform === 'telegram') {
            return telegramConectado
                ? { tom: 'ok', texto: t('channels.details.status.connected', 'Conectado') }
                : { tom: 'neutral', texto: t('channels.details.status.disconnected', 'Não conectado') };
        }
        if (canal.platform === 'instagram') {
            if (!contaVinculada) return { tom: 'neutral', texto: t('channels.details.status.disconnected', 'Não conectado') };
            return instagramMensageriaAtiva
                ? { tom: 'ok', texto: t('channels.details.status.connected', 'Conectado') }
                : { tom: 'warn', texto: t('channels.details.status.pending', 'Pendente') };
        }
        if (PROVIDER_POR_PLATFORM[canal.platform]) {
            return contaVinculada
                ? { tom: 'ok', texto: t('channels.details.status.connected', 'Conectado') }
                : { tom: 'neutral', texto: t('channels.details.status.disconnected', 'Não conectado') };
        }
        return null;
    }

    // Segunda linha do topo: plataforma + o identificador que o cliente reconhece
    // (número, @usuário do bot ou da conta), quando houver algum.
    function identificadorCanal() {
        if (canal.platform === 'telegram' && config.telegram?.botUsername) return `@${config.telegram.botUsername}`;
        if (contaVinculada?.account_info?.username) return `@${contaVinculada.account_info.username}`;
        if (contaVinculada?.account_info?.displayPhoneNumber) return contaVinculada.account_info.displayPhoneNumber;
        if (contaVinculada?.account_name) return contaVinculada.account_name;
        return null;
    }

    // Linha secundária sob o nome da conta vinculada. Contas WABA sem nome
    // amigável trazem o próprio telefone em account_name — nesse caso a linha
    // secundária repetiria o nome, então é omitida.
    function identidadeMeta(conta) {
        if (conta?.account_info?.username) return `@${conta.account_info.username}`;
        const telefone = conta?.account_info?.displayPhoneNumber;
        if (telefone && telefone !== conta?.account_name) return telefone;
        if (!telefone) return conta?.provider || null;
        return null;
    }

    const conexao = statusConexao();
    const identificador = identificadorCanal();
    const plataformaUi = PLATAFORMA_UI[canal.platform] || PLATAFORMA_PADRAO;

    // Ponto + rótulo do estado de conexão na linha abaixo do título. É a única
    // marcação de estado do topo — não repetimos badge e ponto para o mesmo fato.
    const statusTom = {
        ok: { dot: D.statusDotOk, text: D.statusTextOk },
        warn: { dot: D.statusDotWarn, text: D.statusTextWarn },
        neutral: { dot: D.statusDotNeutral, text: D.statusTextNeutral },
    };

    // Blocos de conexão da plataforma. Vão para dentro da aba "Configuração" do
    // CanaisConfig: é lá que a configuração do canal é lida, e a barra de abas
    // fica encostada na barra de topo como no design.
    const temBlocosConexao =
        canal?.platform === 'telegram' ||
        (modoAvancado && (canal?.platform === 'instagram' || canal?.platform === 'messenger'));

    const blocoVinculoIdentidade = vinculoUi ? (
        <div className={D.identityLinkBlock}>
            <div className={D.cardHead}>
                <div className="min-w-0">
                    <h4 className={D.sectionTitle}>{t('channels.details.meta.linkTitle', 'Conta Social Vinculada')}</h4>
                    <p className={D.sectionSubtitle}>
                        {t(`channels.details.meta.linkSubtitle_${canal.platform}`)}
                    </p>
                </div>
                {contaVinculada && (
                    <span className={`${D.badge} ${D.badgeOk}`}>
                        <span className={`${D.badgeDot} ${D.badgeDotOk}`} />
                        {t('channels.details.meta.linkedBadge', 'Vinculada')}
                    </span>
                )}
            </div>

            <div className="mt-4">
                {contaVinculada ? (
                    <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                        <div className={`${D.identityRow} flex-1 min-w-0`}>
                            <span className={D.identityIcon} style={{ background: plataformaUi.fundo }}>
                                {contaVinculada.profile_picture ? (
                                    <img
                                        src={contaVinculada.profile_picture}
                                        alt={contaVinculada.account_name}
                                        className="w-full h-full object-cover"
                                    />
                                ) : (
                                    <Iconify icon={vinculoUi.iconeConta} width={22} className="text-[#71717a]" />
                                )}
                            </span>
                            <div className="min-w-0">
                                <p className={D.identityName}>{contaVinculada.account_name}</p>
                                {identidadeMeta(contaVinculada) && (
                                    <p className={D.identityMeta}>{identidadeMeta(contaVinculada)}</p>
                                )}
                            </div>
                        </div>
                        <button
                            type="button"
                            disabled={vinculando}
                            onClick={desvincularContaSocial}
                            className={`${D.dangerButton} shrink-0`}
                        >
                            {vinculando && <Iconify icon="line-md:loading-twotone-loop" width={15} />}
                            {t('channels.details.meta.unlinkButton', 'Desvincular')}
                        </button>
                    </div>
                ) : canal.mkt_account_id ? (
                    <div className={D.noticeWarn}>
                        <p>
                            {t(
                                'channels.details.meta.linkedAccountMissing',
                                'A conta vinculada não foi encontrada — pode ter sido desconectada em Contas Sociais.'
                            )}
                        </p>
                        <button
                            type="button"
                            disabled={vinculando}
                            onClick={desvincularContaSocial}
                            className={`${D.warnButton} mt-3`}
                        >
                            {vinculando && <Iconify icon="line-md:loading-twotone-loop" width={15} />}
                            {t('channels.details.meta.clearLinkButton', 'Limpar vínculo')}
                        </button>
                    </div>
                ) : (
                    <div className="flex flex-col md:flex-row gap-2.5">
                        <select
                            value={contaSelecionada}
                            onChange={(e) => setContaSelecionada(e.target.value)}
                            disabled={loadingContasSociais}
                            className={`${D.select} md:max-w-[440px]`}
                        >
                            <option value="">
                                {loadingContasSociais
                                    ? t('channels.details.meta.loadingAccounts', 'Carregando contas...')
                                    : contasSociais.length === 0
                                      ? t('channels.details.meta.noAccountsAvailable', 'Nenhuma conta social disponível')
                                      : t('channels.details.meta.selectAccount', 'Selecione uma conta')}
                            </option>
                            {contasSociais.map((c) => {
                                const coex = c.account_info?.coexistence || {};
                                const isCoex = c.account_info?.signupMode === 'coexistence';
                                const syncPending = isCoex && !(coex.contacts?.requestId && coex.history?.requestId);
                                let syncExpired = false;
                                if (syncPending && (coex.syncDeadlineAt || coex.onboardedAt)) {
                                    const deadline = coex.syncDeadlineAt
                                        ? new Date(coex.syncDeadlineAt)
                                        : new Date(new Date(coex.onboardedAt).getTime() + 24 * 60 * 60 * 1000);
                                    syncExpired = !Number.isNaN(deadline.getTime()) && Date.now() > deadline.getTime();
                                }
                                const emUso =
                                    Boolean(c.canal_vinculado) && String(c.canal_vinculado._id) !== String(canal._id);
                                return (
                                    <option key={c._id} value={c._id} disabled={emUso || syncExpired}>
                                        {c.account_name}
                                        {emUso
                                            ? t('channels.details.meta.accountInUseBy', { name: c.canal_vinculado.nome })
                                            : syncExpired
                                              ? t('channels.details.meta.syncWindowExpired')
                                              : syncPending
                                                ? t('channels.details.meta.linkWithin24h')
                                                : ''}
                                    </option>
                                );
                            })}
                        </select>
                        <button
                            type="button"
                            disabled={vinculando || !contaSelecionada}
                            onClick={vincularContaSocial}
                            className={D.primaryButton}
                        >
                            {vinculando && <Iconify icon="line-md:loading-twotone-loop" width={15} />}
                            {t('channels.details.meta.linkButton', 'Vincular')}
                        </button>
                    </div>
                )}

                {!contaVinculada && !canal.mkt_account_id && contasSociais.length === 0 && !loadingContasSociais && (
                    <p className={D.hint}>
                        {t(`channels.details.meta.noAccounts_${canal.platform}`)}{' '}
                        <a href="/dashboard/configuracoes/contas-sociais" className={D.link}>
                            {t('channels.details.meta.connectAccountsLink', 'Conectar em Contas Sociais')}
                        </a>
                        .
                    </p>
                )}

                {canal.platform === 'instagram' && contaVinculada && (
                    <div className={D.section}>
                        <div className={D.cardHead}>
                            <div className="min-w-0">
                                <h4 className={D.sectionTitle}>
                                    {t('channels.details.meta.igMessagingTitle', 'Mensagens do Instagram')}
                                </h4>
                                <p className={D.sectionSubtitle}>
                                    {t(
                                        'channels.details.meta.igMessagingSubtitle',
                                        'A conta vinculada já roteia os webhooks, mas enviar/receber mensagens exige um login separado do Instagram (Instagram Login for Business).'
                                    )}
                                </p>
                            </div>
                            {instagramMensageriaAtiva ? (
                                <span className={`${D.badge} ${D.badgeOk}`}>
                                    <span className={`${D.badgeDot} ${D.badgeDotOk}`} />
                                    {t('channels.details.meta.igMessagingConnected', 'Conectado')}
                                </span>
                            ) : (
                                <span className={`${D.badge} ${D.badgeWarn}`}>
                                    <span className={`${D.badgeDot} ${D.badgeDotWarn}`} />
                                    {t('channels.details.meta.igMessagingPending', 'Pendente')}
                                </span>
                            )}
                        </div>
                        {!instagramMensageriaAtiva && (
                            <button type="button" onClick={conectarInstagramMessaging} className={`${D.primaryButton} mt-3.5`}>
                                {t('channels.details.meta.igMessagingButton', 'Conectar mensagens')}
                            </button>
                        )}
                    </div>
                )}
            </div>

            {canal?.platform !== 'whatsapp_business' && (
                <button type="button" onClick={() => setModoAvancado((v) => !v)} className={`${D.linkMuted} mt-4 block`}>
                    {modoAvancado
                        ? t('channels.details.meta.hideAdvanced', 'Ocultar configuração avançada')
                        : t('channels.details.meta.showAdvanced', 'Configuração avançada (inserir token manualmente)')}
                </button>
            )}
        </div>
    ) : null;

    const blocosConexao = (
        <>
            {canal?.platform === 'telegram' &&
                (telegramConectado ? (
                    <div className={D.cardFlush}>
                        <div className={D.connectionHead}>
                            <div className={D.identityRow}>
                                <span className={D.identityIcon} style={{ background: plataformaUi.fundo }}>
                                    <Iconify icon="logos:telegram" width={24} />
                                </span>
                                <div className="min-w-0">
                                    <p className={D.identityName}>@{config.telegram?.botUsername}</p>
                                    <p className={D.identityMeta}>
                                        {config.telegram?.botName
                                            ? t('channels.details.telegram.connectedMeta', '{{name}} · bot conectado via BotFather', {
                                                  name: config.telegram.botName,
                                              })
                                            : t('channels.details.telegram.connectedMetaFallback', 'Bot conectado via BotFather')}
                                    </p>
                                </div>
                            </div>
                            <button
                                type="button"
                                disabled={telegramLoading}
                                onClick={desconectarTelegram}
                                className={D.dangerButton}
                            >
                                {telegramLoading && <Iconify icon="line-md:loading-twotone-loop" width={15} />}
                                {t('channels.details.telegram.disconnectButton', 'Desconectar')}
                            </button>
                        </div>

                        {/* Telegram Business: opcional. Nasce fechado — quem não usa
                            não precisa rolar por cima de quatro passos. */}
                        <div className={D.disclosure}>
                            <button
                                type="button"
                                onClick={() => setBusinessAberto((v) => !v)}
                                aria-expanded={businessAberto}
                                className={D.disclosureButton}
                            >
                                <span
                                    className={`${D.disclosureCaret} ${businessAberto ? D.disclosureCaretOpen : ''}`}
                                    aria-hidden="true"
                                >
                                    ▶
                                </span>
                                <span className="flex-1 min-w-0">
                                    <span className="block text-[14px] font-semibold text-[#18181b]">
                                        {t('channels.details.telegram.businessTitle', 'Atender pelo seu número comercial')}
                                    </span>
                                    <span className="block mt-0.5 text-[13px] text-[#71717a] leading-[1.5]">
                                        {t(
                                            'channels.details.telegram.businessSubtitleShort',
                                            'Opcional. O cliente segue falando com o seu número e o bot responde por trás.'
                                        )}
                                    </span>
                                </span>
                                <span
                                    className={`${D.badge} ${telegramBusiness?.businessEnabled ? D.badgeOk : D.badgeNeutral}`}
                                >
                                    {telegramBusiness?.businessEnabled
                                        ? t('channels.details.telegram.linkedBadge', 'Vinculado')
                                        : t('channels.details.telegram.notLinkedBadge', 'Não vinculado')}
                                </span>
                            </button>

                            {businessAberto &&
                                (telegramBusiness?.businessEnabled ? (
                                    <div className={`${D.disclosureBody} text-[13px] text-[#3f3f46] leading-[1.55]`}>
                                        <p>
                                            {t('channels.details.telegram.accountLabel', 'Conta:')}{' '}
                                            <b className="font-semibold text-[#18181b]">
                                                {telegramBusiness.businessUsername
                                                    ? `@${telegramBusiness.businessUsername}`
                                                    : telegramBusiness.businessName || '—'}
                                            </b>
                                        </p>
                                        {telegramBusiness.businessCanReply === false && (
                                            <p className="text-[#b54708]">
                                                <Trans t={t} i18nKey="channels.details.telegram.noReplyPermission" components={{ b: <b /> }}>
                                                    O bot está vinculado mas <b>sem permissão para responder</b>. Ative a permissão de
                                                    resposta nas configurações do chatbot no Telegram.
                                                </Trans>
                                            </p>
                                        )}
                                        <p className="text-[#a1a1aa] text-[12px]">
                                            {t(
                                                'channels.details.telegram.unlinkHint',
                                                'Para desvincular, remova o bot em Configurações → Telegram Business → Chatbots, no app.'
                                            )}
                                        </p>
                                    </div>
                                ) : (
                                    <div className={D.disclosureBody}>
                                        <div className={D.stepRow}>
                                            <span className={D.stepNumber}>1</span>
                                            <p className={D.stepText}>
                                                <Trans t={t} i18nKey="channels.details.telegram.businessStep1" components={{ b: <b className="font-semibold" /> }}>
                                                    A conta comercial precisa ter <b>Telegram Premium</b> — exigência do Telegram.
                                                </Trans>
                                            </p>
                                        </div>
                                        <div className={D.stepRow}>
                                            <span className={D.stepNumber}>2</span>
                                            <p className={D.stepText}>
                                                <Trans t={t} i18nKey="channels.details.telegram.businessStep2" components={{ b: <b className="font-semibold" /> }}>
                                                    No app: <b>Configurações → Telegram Business → Chatbots</b>.
                                                </Trans>
                                            </p>
                                        </div>
                                        <div className={D.stepRow}>
                                            <span className={D.stepNumber}>3</span>
                                            <p className={D.stepText}>
                                                <Trans
                                                    t={t}
                                                    i18nKey="channels.details.telegram.businessStep3"
                                                    values={{ botUsername: config.telegram?.botUsername }}
                                                    components={{ b: <b className="font-semibold" /> }}
                                                >
                                                    Informe <b>@{{ botUsername: config.telegram?.botUsername }}</b> e permita que o bot responda.
                                                </Trans>
                                            </p>
                                        </div>
                                        <p className={D.stepFoot}>
                                            {t('channels.details.telegram.businessStep4', 'Pronto — esta seção passa a mostrar “Vinculado” sozinha.')}
                                        </p>
                                    </div>
                                ))}
                        </div>
                    </div>
                ) : (
                    <div className={D.card}>
                        <h3 className={D.cardTitle}>
                            {t('channels.details.telegram.connectionTitle', 'Conexão do Bot do Telegram')}
                        </h3>
                        <p className={D.cardSubtitle}>
                            {t(
                                'channels.details.telegram.connectionSubtitle',
                                'Conecte um bot criado no @BotFather. O token é a única credencial necessária.'
                            )}
                        </p>

                        <div className="mt-5 flex flex-col gap-4">
                            <div className="flex flex-col gap-3">
                                <div className={D.stepRow}>
                                    <span className={D.stepNumber}>1</span>
                                    <p className={D.stepText}>
                                        <Trans t={t} i18nKey="channels.details.telegram.step1" components={{ b: <b className="font-semibold" />, code: <code /> }}>
                                            No Telegram, procure por <b>@BotFather</b> e envie <code>/start</code>.
                                        </Trans>
                                    </p>
                                </div>
                                <div className={D.stepRow}>
                                    <span className={D.stepNumber}>2</span>
                                    <p className={D.stepText}>
                                        <Trans t={t} i18nKey="channels.details.telegram.step2" components={{ b: <b className="font-semibold" />, code: <code /> }}>
                                            Envie <code>/newbot</code> e siga as instruções para criar o bot (ou <code>/mybots</code> →{' '}
                                            <b>API Token</b> para reaproveitar um bot existente).
                                        </Trans>
                                    </p>
                                </div>
                                <div className={D.stepRow}>
                                    <span className={D.stepNumber}>3</span>
                                    <p className={D.stepText}>
                                        {t('channels.details.telegram.step3', 'Copie o token da API que o BotFather enviar e cole no campo abaixo.')}
                                    </p>
                                </div>
                                <div className={D.stepRow}>
                                    <span className={D.stepNumber}>4</span>
                                    <p className={D.stepText}>
                                        {t('channels.details.telegram.step4', 'Após conectar, toda mensagem enviada ao bot aparece aqui na caixa de entrada.')}
                                    </p>
                                </div>
                            </div>

                            <div>
                                <label className={D.label} htmlFor="telegram-bot-token">
                                    {t('channels.details.telegram.tokenLabel', 'Token da API do bot')}{' '}
                                    <span className={D.required}>{t('channels.details.requiredLabel', '(obrigatório)')}</span>
                                </label>
                                <div className="flex flex-col md:flex-row gap-2.5">
                                    <input
                                        id="telegram-bot-token"
                                        type="password"
                                        value={telegramToken}
                                        onChange={(e) => setTelegramToken(e.target.value)}
                                        placeholder={t(
                                            'channels.details.telegram.tokenPlaceholder',
                                            'Ex.: 123456789:AAF-xxxxxxxxxxxxxxxxxxxxxxxxxxxx'
                                        )}
                                        autoComplete="off"
                                        className={`${D.input} ${D.inputMono} md:max-w-[440px]`}
                                    />
                                    <button
                                        type="button"
                                        disabled={telegramLoading}
                                        onClick={conectarTelegram}
                                        className={D.primaryButton}
                                    >
                                        {telegramLoading && <Iconify icon="line-md:loading-twotone-loop" width={15} />}
                                        {t('channels.details.telegram.saveConnectButton', 'Salvar e conectar')}
                                    </button>
                                </div>
                                <p className={D.hint}>
                                    {t(
                                        'channels.details.telegram.tokenHint',
                                        'O token dá controle total sobre o bot — ele fica guardado apenas no servidor e nunca é exibido de volta nesta tela.'
                                    )}
                                </p>
                            </div>
                        </div>
                    </div>
                ))}

            {modoAvancado && (canal?.platform === 'instagram' || canal?.platform === 'messenger') && (
                <div className={D.card}>
                    <h3 className={D.cardTitle}>
                        {t('channels.details.meta.connectionTitle', 'Conexão Manual ({{platformName}})', {
                            platformName: canal.platform === 'instagram' ? 'Instagram Direct' : 'Facebook Messenger',
                        })}
                    </h3>
                    <p className={D.cardSubtitle}>
                        {t(
                            'channels.details.meta.connectionSubtitle',
                            'Preencha caso queira conectar sem o login do Facebook. O webhook localiza o canal por {{idFieldName}}.',
                            { idFieldName }
                        )}
                    </p>

                    <div className={`${D.grid2} mt-5`}>
                        {canal.platform === 'instagram' && (
                            <div className={D.field}>
                                <label className={D.label} htmlFor="meta-instagram-user-id">
                                    {t('channels.details.meta.instagramUserIdLabel')}{' '}
                                    <span className={D.required}>{t('channels.details.requiredLabel')}</span>
                                </label>
                                <input
                                    id="meta-instagram-user-id"
                                    type="text"
                                    value={config.meta?.instagram?.instagramUserId || ''}
                                    onChange={(e) => updateMetaField('instagram', 'instagramUserId', e.target.value)}
                                    placeholder={t('channels.details.meta.instagramUserIdPlaceholder', 'Ex.: 17841457003786477')}
                                    className={D.input}
                                />
                            </div>
                        )}

                        <div className={D.field}>
                            <label className={D.label} htmlFor="meta-page-id">
                                {t('channels.details.meta.pageIdLabel')}{' '}
                                {canal.platform === 'messenger' && (
                                    <span className={D.required}>{t('channels.details.requiredLabel')}</span>
                                )}
                            </label>
                            <input
                                id="meta-page-id"
                                type="text"
                                value={config.meta?.[canal.platform]?.pageId || ''}
                                onChange={(e) => updateMetaField(canal.platform, 'pageId', e.target.value)}
                                placeholder={t('channels.details.meta.pageIdPlaceholder', 'ID da Página do Facebook')}
                                className={D.input}
                            />
                        </div>

                        <div className={`${D.field} md:col-span-2`}>
                            <label className={D.label} htmlFor="meta-access-token">
                                {t('channels.details.meta.accessTokenLabel')}
                            </label>
                            <input
                                id="meta-access-token"
                                type="text"
                                value={config.meta?.[canal.platform]?.accessToken || ''}
                                onChange={(e) => updateMetaField(canal.platform, 'accessToken', e.target.value)}
                                placeholder={t('channels.details.meta.accessTokenPlaceholder', 'Token de acesso da Página')}
                                className={`${D.input} ${D.inputMono}`}
                            />
                        </div>
                    </div>

                    <p className={D.footnote}>
                        <Trans t={t} i18nKey="channels.details.meta.tokenHint" values={{ idFieldName }} components={{ b: <b /> }}>
                            O <b>Access Token</b> é necessário para <b>enviar</b> mensagens e buscar perfil. Para apenas <b>receber</b>,
                            basta o {{ idFieldName }}. Clique em "Salvar Configurações" no topo para aplicar.
                        </Trans>
                    </p>
                </div>
            )}
        </>
    );

    // O cabeçalho é entregue ao CanaisConfig em vez de renderizado aqui: no
    // design ele e a barra de abas são um bloco fixo único, e as abas moram lá.
    const cabecalho = (
        <div className={D.topbar}>
            <div className={D.topbarIdentity}>
                <button
                    type="button"
                    title={t('channels.details.backToChannels', 'Voltar para canais')}
                    onClick={() => navigate('/dashboard/configuracoes/canais')}
                    className={D.backButton}
                >
                    <Iconify icon="majesticons:arrow-left" width={17} />
                </button>
                <span className={D.platformTile} style={{ background: plataformaUi.fundo }}>
                    <Iconify icon={plataformaUi.icone} width={24} style={plataformaUi.cor ? { color: plataformaUi.cor } : undefined} />
                </span>
                <div className="min-w-0 flex-1 pt-px">
                    <div className={D.titleRow}>
                        <h1 className={D.title}>{canal?.nome || t('channels.details.newChannelTitle', 'Novo Canal')}</h1>
                        {identificador && <span className={D.handle}>{identificador}</span>}
                    </div>
                    <div className={D.statusRow}>
                        {conexao && (
                            <>
                                <span className={`${D.statusDot} ${statusTom[conexao.tom].dot}`} />
                                <span className={`${D.statusText} ${statusTom[conexao.tom].text}`}>{conexao.texto}</span>
                                <span className={D.statusSep} aria-hidden="true">
                                    ·
                                </span>
                            </>
                        )}
                        <span className={D.statusMeta}>{plataformaNome}</span>
                    </div>
                </div>
            </div>

            <div className={D.topbarActions}>
                {alteracoesPendentes ? (
                    <>
                        <span className={D.dirtyNote}>{t('channels.details.unsavedChanges', 'Alterações não salvas')}</span>
                        <button type="button" onClick={descartarAlteracoes} disabled={loading} className={D.ghostButton}>
                            {t('channels.details.discardButton', 'Descartar')}
                        </button>
                    </>
                ) : (
                    <span className={D.savedNote}>{rotuloSalvo()}</span>
                )}
                <button type="button" onClick={createChannel} disabled={loading || !podeSalvar} className={D.primaryButton}>
                    {loading && <Iconify icon="line-md:loading-twotone-loop" width={15} />}
                    {isEdit
                        ? t('channels.details.saveButton', 'Salvar configurações')
                        : t('channels.details.createButton', 'Criar canal')}
                </button>
            </div>
        </div>
    );

    if (carregandoInicial) return <CanalDetalheSkeleton />;

    return (
        <div className={D.root} style={{ fontFamily: D.fontStack }}>
            <CanaisConfig
                cliente={cliente}
                user={user}
                listUsers={listUsers}
                listSectors={listSectors}
                isEdit={isEdit}
                config={config}
                canal={makeChannel}
                header={cabecalho}
                connectionBlocks={temBlocosConexao ? blocosConexao : null}
                identityLinkBlock={blocoVinculoIdentidade}
                onChange={(e, newValue) => handleOnChangeChannel(e, newValue)}
                onChangeAutocomplete={(e, newValue, name) => handleOnChangeAutomCompleteChannel(e, newValue, name)}
                changeAgent={(value) => {
                    setConfig({ ...config, dialogflow: { ...config.dialogflow, projectId: value } });
                }}
                onChangeChannel={(value) => {
                    if (value.target === 'channelType') {
                        setCanal({ ...canal, platform: value.value });
                    } else if (value.target === 'webhookType') {
                        setCanal({ ...canal, webhook_type: value.value });
                    }
                }}
            />
        </div>
    );
}
